// ============================================================
// CalendarContextMenu.tsx
// Right-click add-menu for the Diary (month cells + day/week slots). A
// small Casevo-styled menu positioned at the cursor, viewport-clamped +
// flipped like CaseContextMenu so it never overflows off-screen. The
// parent supplies the item list (label + action); this component only
// renders + positions + closes (outside-click / Escape / after an action).
// ============================================================
import React, { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { MENU_SURFACE, MenuGroupHead, MenuItem, MenuSep } from '../shared/menuUi'

export interface CalendarMenuItem {
  label: string
  onClick: () => void
  // Optional leading glyph for quick scanning — a text glyph or a Feather
  // icon element (calendarIcons.tsx). Decorative (aria-hidden).
  icon?: React.ReactNode
  // Render as a top separator before this item.
  separated?: boolean
  // Destructive (Delete…) → danger tone (menuUi MenuItem tone="danger").
  danger?: boolean
}

interface CalendarContextMenuProps {
  x: number
  y: number
  items: CalendarMenuItem[]
  // Display-only group heading (mockup CA6261 §C) — e.g. "Thu 15 Oct · 10:00".
  title?: string
  onClose: () => void
}

const CalendarContextMenu: React.FC<CalendarContextMenuProps> = ({ x, y, items, title, onClose }) => {
  const ref = useRef<HTMLDivElement>(null)

  // Viewport clamp / flip — measure the rendered menu, then flip above /
  // shift left if it would overflow, and clamp into an 8px margin. Mirrors
  // CaseContextMenu so both right-click menus behave identically.
  const [pos, setPos] = useState<{ top: number; left: number }>({ top: y, left: x })
  useLayoutEffect(() => {
    const el = ref.current
    if (!el) return
    const MARGIN = 8
    const { width, height } = el.getBoundingClientRect()
    const vw = window.innerWidth
    const vh = window.innerHeight
    let left = x + width + MARGIN > vw ? x - width : x
    left = Math.min(Math.max(left, MARGIN), Math.max(MARGIN, vw - width - MARGIN))
    let top = y + height + MARGIN > vh ? y - height : y
    top = Math.min(Math.max(top, MARGIN), Math.max(MARGIN, vh - height - MARGIN))
    setPos({ top, left })
  }, [x, y, items.length])

  // Close on click-outside + Escape.
  useEffect(() => {
    const onDown = (e: MouseEvent): void => {
      if (ref.current && !ref.current.contains(e.target as Node)) onClose()
    }
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('mousedown', onDown)
    window.addEventListener('keydown', onKey)
    return () => {
      window.removeEventListener('mousedown', onDown)
      window.removeEventListener('keydown', onKey)
    }
  }, [onClose])

  const run = (fn: () => void) => () => { fn(); onClose() }
  // Shared menu language (menuUi.tsx — mockup CA6261), same as CaseContextMenu.
  return (
    <div
      ref={ref}
      role="menu"
      style={{ top: pos.top, left: pos.left }}
      className={`fixed z-[200] ${MENU_SURFACE}`}
      onContextMenu={(e) => e.preventDefault()}
    >
      {title && <MenuGroupHead>{title}</MenuGroupHead>}
      {items.map((it, i) => (
        <React.Fragment key={i}>
          {it.separated && <MenuSep />}
          <MenuItem icon={it.icon} label={it.label} tone={it.danger ? 'danger' : 'default'} onClick={run(it.onClick)} />
        </React.Fragment>
      ))}
    </div>
  )
}

export default CalendarContextMenu
